"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { ChevronDown, GitCompare, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { MedicineDetails } from "@/entities/medicine/api/medicines";
import { compareMedicinesByDisplayName, translateDosageForm } from "@/entities/medicine/lib/medicine-label";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import { filterToggleVariants } from "@/shared/ui/filter-toggle";
import { DosageFormIcon } from "./dosage-form-icon";
import { SharedSpecs } from "./shared-specs";
import { EmptyState } from "./ui";
import { buildGroupedFinderResults, formatVariantSpecs, type GroupedFinderResult } from "./utils";
type MedicineAnaloguesBrowserProps = {
  analogues: MedicineDetails[];
  loading: boolean;
  error: string | null;
  resetKey?: string | number | null;
  selectedMedicineId?: string | number | null;
  onSelectedMedicineChange?: (medicine: MedicineDetails | null) => void;
  onActivateMedicine?: (medicine: MedicineDetails) => void;
  selectFirstOnLoad?: boolean;
  actionLabel?: string;
  onAction?: (medicine: MedicineDetails) => void;
  autoFocus?: boolean;
};
type DosageFormOption = {
  form: string;
  count: number;
  label: string;
};
export function MedicineAnaloguesBrowser(t0) {
  const $ = _c(94);
  const {
    analogues,
    loading,
    error,
    resetKey: t1,
    selectedMedicineId,
    onSelectedMedicineChange,
    onActivateMedicine,
    selectFirstOnLoad: t2,
    actionLabel,
    onAction,
    autoFocus: t3
  } = t0;
  const resetKey = t1 === undefined ? null : t1;
  const selectFirstOnLoad = t2 === undefined ? true : t2;
  const autoFocus = t3 === undefined ? false : t3;
  const {
    t
  } = useTranslation();
  const listContainerRef = React.useRef(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = new Set();
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const [expandedGroups, setExpandedGroups] = React.useState(t4);
  let t5;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set();
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const [dosageFormFilter, setDosageFormFilter] = React.useState(t5);
  const [internalSelectedId, setInternalSelectedId] = React.useState(null);
  const activeId = selectedMedicineId ?? internalSelectedId;
  let t6;
  if ($[2] !== analogues) {
    const counts = new Map();
    for (const medicine of analogues) {
      const form = medicine.dosage_form;
      if (!form) {
        continue;
      }
      counts.set(form, (counts.get(form) ?? 0) + 1);
    }
    t6 = [...counts.entries()].map(_temp).sort(_temp2);
    $[2] = analogues;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const dosageFormOptions = t6;
  let t7;
  if ($[4] !== analogues || $[5] !== dosageFormFilter) {
    const next = dosageFormFilter.size === 0 ? [...analogues] : analogues.filter(medicine_0 => medicine_0.dosage_form && dosageFormFilter.has(medicine_0.dosage_form));
    t7 = next.sort(compareMedicinesByDisplayName);
    $[4] = analogues;
    $[5] = dosageFormFilter;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const filteredAnalogues = t7;
  let t8;
  if ($[7] !== filteredAnalogues) {
    t8 = buildGroupedFinderResults(filteredAnalogues);
    $[7] = filteredAnalogues;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const groupedAnalogues = t8;
  let list;
  if ($[9] !== expandedGroups || $[10] !== groupedAnalogues) {
    list = [];
    for (const group of groupedAnalogues) {
      if (group.items.length === 1) {
        list.push(group.items[0] as MedicineDetails);
        continue;
      }
      if (!expandedGroups.has(group.label)) {
        continue;
      }
      for (const item of group.items) {
        list.push(item as MedicineDetails);
      }
    }
    $[9] = expandedGroups;
    $[10] = groupedAnalogues;
    $[11] = list;
  } else {
    list = $[11];
  }
  const visibleMedicines = list;
  let t9;
  if ($[12] !== onSelectedMedicineChange || $[13] !== selectedMedicineId) {
    t9 = medicine_1 => {
      if (selectedMedicineId == null) {
        setInternalSelectedId(medicine_1?.medicine_id ?? null);
      }
      onSelectedMedicineChange?.(medicine_1);
    };
    $[12] = onSelectedMedicineChange;
    $[13] = selectedMedicineId;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const setSelectedMedicine = t9;
  let t10;
  if ($[15] !== selectedMedicineId) {
    t10 = () => {
      setExpandedGroups(new Set());
      setDosageFormFilter(new Set());
      if (selectedMedicineId == null) {
        setInternalSelectedId(null);
      }
    };
    $[15] = selectedMedicineId;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== resetKey || $[18] !== selectedMedicineId) {
    t11 = [resetKey, selectedMedicineId];
    $[17] = resetKey;
    $[18] = selectedMedicineId;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  React.useEffect(t10, t11);
  let t12;
  let t13;
  if ($[20] !== activeId || $[21] !== error || $[22] !== filteredAnalogues || $[23] !== loading || $[24] !== selectFirstOnLoad || $[25] !== setSelectedMedicine) {
    t12 = () => {
      if (loading || error) {
        return;
      }
      if (filteredAnalogues.length === 0) {
        if (activeId != null) {
          setSelectedMedicine(null);
        }
        return;
      }
      if (activeId != null && filteredAnalogues.some(medicine_2 => medicine_2.medicine_id === activeId)) {
        return;
      }
      if (selectFirstOnLoad) {
        setSelectedMedicine(filteredAnalogues[0]);
        return;
      }
      if (activeId != null) {
        setSelectedMedicine(null);
      }
    };
    t13 = [activeId, error, filteredAnalogues, loading, selectFirstOnLoad, setSelectedMedicine];
    $[20] = activeId;
    $[21] = error;
    $[22] = filteredAnalogues;
    $[23] = loading;
    $[24] = selectFirstOnLoad;
    $[25] = setSelectedMedicine;
    $[26] = t12;
    $[27] = t13;
  } else {
    t12 = $[26];
    t13 = $[27];
  }
  React.useEffect(t12, t13);
  let t14;
  let t15;
  if ($[28] !== activeId) {
    t14 = () => {
      if (!listContainerRef.current || activeId == null) {
        return;
      }
      const element = listContainerRef.current.querySelector(`[data-medicine-id="${activeId}"]`);
      element?.scrollIntoView({
        block: "nearest"
      });
    };
    t15 = [activeId];
    $[28] = activeId;
    $[29] = t14;
    $[30] = t15;
  } else {
    t14 = $[29];
    t15 = $[30];
  }
  React.useEffect(t14, t15);
  let t16;
  let t17;
  if ($[31] !== autoFocus || $[32] !== filteredAnalogues.length) {
    t16 = () => {
      if (!autoFocus || !filteredAnalogues.length || !listContainerRef.current) {
        return;
      }
      listContainerRef.current.focus({
        preventScroll: true
      });
    };
    t17 = [autoFocus, filteredAnalogues.length];
    $[31] = autoFocus;
    $[32] = filteredAnalogues.length;
    $[33] = t16;
    $[34] = t17;
  } else {
    t16 = $[33];
    t17 = $[34];
  }
  React.useEffect(t16, t17);
  let t18;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = form_1 => {
      setDosageFormFilter(prev => {
        const next_0 = new Set(prev);
        if (next_0.has(form_1)) {
          next_0.delete(form_1);
        } else {
          next_0.add(form_1);
        }
        return next_0;
      });
    };
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const toggleDosageFormFilter = t18;
  let t19;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = label => {
      setExpandedGroups(prev_0 => {
        const next_1 = new Set(prev_0);
        if (next_1.has(label)) {
          next_1.delete(label);
        } else {
          next_1.add(label);
        }
        return next_1;
      });
    };
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  const toggleGroupExpanded = t19;
  let t20;
  if ($[37] !== activeId || $[38] !== setSelectedMedicine || $[39] !== visibleMedicines) {
    t20 = event => {
      if (!visibleMedicines.length) {
        return;
      }
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") {
        return;
      }
      event.preventDefault();
      const currentIndex = visibleMedicines.findIndex(medicine_3 => medicine_3.medicine_id === activeId);
      const nextIndex = event.key === "ArrowDown" ? currentIndex < visibleMedicines.length - 1 ? currentIndex + 1 : 0 : currentIndex > 0 ? currentIndex - 1 : visibleMedicines.length - 1;
      setSelectedMedicine(visibleMedicines[nextIndex]);
    };
    $[37] = activeId;
    $[38] = setSelectedMedicine;
    $[39] = visibleMedicines;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  const handleListKeyDown = t20;
  let t21;
  if ($[41] !== onActivateMedicine || $[42] !== setSelectedMedicine) {
    t21 = medicine_4 => {
      if (onActivateMedicine) {
        onActivateMedicine(medicine_4);
        return;
      }
      setSelectedMedicine(medicine_4);
    };
    $[41] = onActivateMedicine;
    $[42] = setSelectedMedicine;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const handleActivateMedicine = t21;
  if (loading) {
    let t22;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <div className="text-muted-foreground flex items-center justify-center py-16"><Loader2 className="size-4 animate-spin" /></div>;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    return t22;
  }
  if (error) {
    let t22;
    if ($[45] !== t) {
      t22 = t("medicines.analogues.errorTitle", {
        defaultValue: "Failed to load analogues"
      });
      $[45] = t;
      $[46] = t22;
    } else {
      t22 = $[46];
    }
    let t23;
    if ($[47] !== error || $[48] !== t22) {
      t23 = <div className="p-4"><EmptyState icon={GitCompare} title={t22} description={error} /></div>;
      $[47] = error;
      $[48] = t22;
      $[49] = t23;
    } else {
      t23 = $[49];
    }
    return t23;
  }
  if (analogues.length === 0) {
    let t22;
    if ($[50] !== t) {
      t22 = t("medicines.analogues.emptyTitle", {
        defaultValue: "No analogues found"
      });
      $[50] = t;
      $[51] = t22;
    } else {
      t22 = $[51];
    }
    let t23;
    if ($[52] !== t) {
      t23 = t("medicines.analogues.emptyDescription", {
        defaultValue: "No matching medicines by ATC code or international name."
      });
      $[52] = t;
      $[53] = t23;
    } else {
      t23 = $[53];
    }
    let t24;
    if ($[54] !== t22 || $[55] !== t23) {
      t24 = <div className="p-4"><EmptyState icon={GitCompare} title={t22} description={t23} /></div>;
      $[54] = t22;
      $[55] = t23;
      $[56] = t24;
    } else {
      t24 = $[56];
    }
    return t24;
  }
  if (filteredAnalogues.length === 0) {
    let t22;
    if ($[57] !== dosageFormFilter || $[58] !== dosageFormOptions) {
      t22 = dosageFormOptions.length > 1 ? <DosageFormFilterBar options={dosageFormOptions} active={dosageFormFilter} onToggle={toggleDosageFormFilter} /> : null;
      $[57] = dosageFormFilter;
      $[58] = dosageFormOptions;
      $[59] = t22;
    } else {
      t22 = $[59];
    }
    let t23;
    if ($[60] !== t) {
      t23 = t("medicines.analogues.noMatchesTitle", {
        defaultValue: "No matches for filter"
      });
      $[60] = t;
      $[61] = t23;
    } else {
      t23 = $[61];
    }
    let t24;
    if ($[62] !== t) {
      t24 = t("medicines.analogues.noMatchesDescription", {
        defaultValue: "Try clearing the dosage form filter to see all analogues."
      });
      $[62] = t;
      $[63] = t24;
    } else {
      t24 = $[63];
    }
    let t25;
    if ($[64] !== t23 || $[65] !== t24) {
      t25 = <div className="p-4"><EmptyState icon={GitCompare} title={t23} description={t24} /></div>;
      $[64] = t23;
      $[65] = t24;
      $[66] = t25;
    } else {
      t25 = $[66];
    }
    let t26;
    if ($[67] !== t22 || $[68] !== t25) {
      t26 = <div className="flex min-h-0 flex-1 flex-col">{t22}{t25}</div>;
      $[67] = t22;
      $[68] = t25;
      $[69] = t26;
    } else {
      t26 = $[69];
    }
    return t26;
  }
  let t22;
  if ($[70] !== dosageFormFilter || $[71] !== dosageFormOptions) {
    t22 = dosageFormOptions.length > 1 ? <DosageFormFilterBar options={dosageFormOptions} active={dosageFormFilter} onToggle={toggleDosageFormFilter} /> : null;
    $[70] = dosageFormFilter;
    $[71] = dosageFormOptions;
    $[72] = t22;
  } else {
    t22 = $[72];
  }
  let t23;
  if ($[73] !== actionLabel || $[74] !== activeId || $[75] !== expandedGroups || $[76] !== groupedAnalogues || $[77] !== handleActivateMedicine || $[78] !== onAction) {
    let t24;
    if ($[80] !== actionLabel || $[81] !== activeId || $[82] !== expandedGroups || $[83] !== handleActivateMedicine || $[84] !== onAction) {
      t24 = group_0 => {
        if (group_0.items.length === 1) {
          const medicine_5 = group_0.items[0] as MedicineDetails;
          return <AnalogueRow key={medicine_5.medicine_id ?? medicine_5.id} medicine={medicine_5} group={group_0} selected={medicine_5.medicine_id === activeId} actionLabel={actionLabel} onActivate={() => handleActivateMedicine(medicine_5)} onAction={onAction ? () => onAction(medicine_5) : undefined} />;
        }
        return <AnalogueGroup key={group_0.label} group={group_0} expanded={expandedGroups.has(group_0.label)} selectedId={activeId} actionLabel={actionLabel} onToggle={() => toggleGroupExpanded(group_0.label)} onActivate={handleActivateMedicine} onAction={onAction} />;
      };
      $[80] = actionLabel;
      $[81] = activeId;
      $[82] = expandedGroups;
      $[83] = handleActivateMedicine;
      $[84] = onAction;
      $[85] = t24;
    } else {
      t24 = $[85];
    }
    t23 = groupedAnalogues.map(t24);
    $[73] = actionLabel;
    $[74] = activeId;
    $[75] = expandedGroups;
    $[76] = groupedAnalogues;
    $[77] = handleActivateMedicine;
    $[78] = onAction;
    $[79] = t23;
  } else {
    t23 = $[79];
  }
  let t24;
  if ($[86] !== t23) {
    t24 = <ul className="divide-y">{t23}</ul>;
    $[86] = t23;
    $[87] = t24;
  } else {
    t24 = $[87];
  }
  let t25;
  if ($[88] !== handleListKeyDown || $[89] !== t24) {
    t25 = <div ref={listContainerRef} onKeyDown={handleListKeyDown} tabIndex={0} className="min-h-0 flex-1 overflow-y-auto outline-none">{t24}</div>;
    $[88] = handleListKeyDown;
    $[89] = t24;
    $[90] = t25;
  } else {
    t25 = $[90];
  }
  let t26;
  if ($[91] !== t22 || $[92] !== t25) {
    t26 = <div className="flex min-h-0 flex-1 flex-col">{t22}{t25}</div>;
    $[91] = t22;
    $[92] = t25;
    $[93] = t26;
  } else {
    t26 = $[93];
  }
  return t26;
}
function _temp2(a, b) {
  return a.label.localeCompare(b.label);
}
function _temp(t0) {
  const [form_0, count] = t0;
  return {
    form: form_0,
    count,
    label: translateDosageForm(form_0) ?? form_0
  };
}
type AnalogueRowProps = {
  medicine: MedicineDetails;
  group: GroupedFinderResult;
  selected: boolean;
  actionLabel?: string;
  onActivate: () => void;
  onAction?: () => void;
  nested?: boolean;
};
function AnalogueRow(t0) {
  const $ = _c(50);
  const {
    medicine,
    group,
    selected,
    actionLabel,
    onActivate,
    onAction,
    nested: t1
  } = t0;
  const nested = t1 === undefined ? false : t1;
  const name = medicine.trade_name || medicine.name;
  const internationalName = medicine.international_name_ru ?? medicine.international_name;
  let t2;
  if ($[0] !== group || $[1] !== medicine) {
    t2 = formatVariantSpecs(medicine, group);
    $[0] = group;
    $[1] = medicine;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const specs = t2;
  let t3;
  if ($[3] !== group || $[4] !== medicine.vendor?.name || $[5] !== nested) {
    t3 = nested && medicine.vendor?.name && !group.visibleAttributes.includes("vendor_name");
    $[3] = group;
    $[4] = medicine.vendor?.name;
    $[5] = nested;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const showVendor = t3;
  const t4 = medicine.medicine_id;
  let t5;
  if ($[7] !== onActivate) {
    t5 = event => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onActivate();
      }
    };
    $[7] = onActivate;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const t6 = nested ? "py-1.5 pr-3 pl-7" : "px-3 py-2";
  const t7 = selected ? "border-l-primary bg-primary/10" : "hover:border-l-border hover:bg-accent/40 border-l-transparent";
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = cn("flex cursor-pointer items-start gap-2 border-l-2 transition-colors", t6, t7);
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const t9 = medicine.dosage_form;
  const t10 = nested ? "mt-0.5 size-4" : "mt-0.5 size-5";
  const t11 = selected ? "text-primary" : "text-muted-foreground";
  let t12;
  if ($[12] !== t10 || $[13] !== t11) {
    t12 = cn("shrink-0 opacity-60", t10, t11);
    $[12] = t10;
    $[13] = t11;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== medicine.dosage_form || $[16] !== t12) {
    t13 = <DosageFormIcon form={t9} className={t12} />;
    $[15] = medicine.dosage_form;
    $[16] = t12;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== name || $[19] !== nested) {
    t14 = nested ? null : <p className="truncate text-sm font-medium capitalize">{name}</p>;
    $[18] = name;
    $[19] = nested;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== internationalName || $[22] !== nested) {
    t15 = !nested && internationalName ? <p className="text-muted-foreground truncate text-xs italic">{internationalName}</p> : null;
    $[21] = internationalName;
    $[22] = nested;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== nested || $[25] !== specs) {
    t16 = specs ? <p className={cn("text-muted-foreground truncate", nested ? "text-xs" : "mt-0.5 text-xs")}>{specs}</p> : null;
    $[24] = nested;
    $[25] = specs;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] !== medicine.vendor || $[28] !== nested) {
    t17 = !nested && medicine.vendor?.name ? <p className="text-muted-foreground mt-0.5 truncate text-xs">{medicine.vendor.name}</p> : null;
    $[27] = medicine.vendor;
    $[28] = nested;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== medicine.vendor?.name || $[31] !== showVendor) {
    t18 = showVendor ? <p className="text-muted-foreground truncate text-xs">{medicine.vendor?.name}</p> : null;
    $[30] = medicine.vendor?.name;
    $[31] = showVendor;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== t14 || $[34] !== t15 || $[35] !== t16 || $[36] !== t17 || $[37] !== t18) {
    t19 = <div className="min-w-0 flex-1">{t14}{t15}{t16}{t17}{t18}</div>;
    $[33] = t14;
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== actionLabel || $[40] !== onAction) {
    t20 = actionLabel && onAction ? <Button type="button" size="sm" variant="outline" className="h-7 shrink-0 px-2 text-xs" onClick={event_0 => {
      event_0.stopPropagation();
      onAction();
    }}>{actionLabel}</Button> : null;
    $[39] = actionLabel;
    $[40] = onAction;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== medicine.medicine_id || $[43] !== onActivate || $[44] !== t13 || $[45] !== t19 || $[46] !== t20 || $[47] !== t5 || $[48] !== t8) {
    t21 = <div role="button" tabIndex={0} data-medicine-id={t4} onClick={onActivate} onKeyDown={t5} className={t8}>{t13}{t19}{t20}</div>;
    $[42] = medicine.medicine_id;
    $[43] = onActivate;
    $[44] = t13;
    $[45] = t19;
    $[46] = t20;
    $[47] = t5;
    $[48] = t8;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  return t21;
}
type AnalogueGroupProps = {
  group: GroupedFinderResult;
  expanded: boolean;
  selectedId: string | number | null;
  actionLabel?: string;
  onToggle: () => void;
  onActivate: (medicine: MedicineDetails) => void;
  onAction?: (medicine: MedicineDetails) => void;
};
function AnalogueGroup(t0) {
  const $ = _c(41);
  const {
    group,
    expanded,
    selectedId,
    actionLabel,
    onToggle,
    onActivate,
    onAction
  } = t0;
  let t1;
  if ($[0] !== group.items) {
    t1 = new Set(group.items.map(_temp3).filter(Boolean));
    $[0] = group.items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = [...t1];
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const dosageForms = t2;
  const t3 = !expanded && "-rotate-90";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("text-muted-foreground mt-0.5 size-4 shrink-0 transition-transform", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <ChevronDown className={t4} />;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== group.label) {
    t6 = <h3 className="min-w-0 truncate text-sm font-semibold capitalize">{group.label}</h3>;
    $[8] = group.label;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== group.internationalName) {
    t7 = group.internationalName ? <span className="text-muted-foreground max-w-25 truncate text-xs italic">{group.internationalName}</span> : null;
    $[10] = group.internationalName;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== group.items.length) {
    t8 = <span className="text-muted-foreground shrink-0 text-xs tabular-nums">×{group.items.length}</span>;
    $[12] = group.items.length;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== dosageForms) {
    t9 = dosageForms.map(_temp4);
    $[14] = dosageForms;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t6 || $[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div className="flex items-baseline gap-2">{t6}{t7}{t8}{t9}</div>;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== group.shared) {
    t11 = <SharedSpecs shared={group.shared} />;
    $[21] = group.shared;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div className="min-w-0 flex-1">{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== expanded || $[27] !== onToggle || $[28] !== t12 || $[29] !== t5) {
    t13 = <button type="button" onClick={onToggle} aria-expanded={expanded} className="bg-muted/30 hover:bg-muted/50 flex w-full items-start gap-2 border-b px-3 py-2 text-left transition-colors">{t5}{t12}</button>;
    $[26] = expanded;
    $[27] = onToggle;
    $[28] = t12;
    $[29] = t5;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== actionLabel || $[32] !== expanded || $[33] !== group || $[34] !== onAction || $[35] !== onActivate || $[36] !== selectedId) {
    t14 = expanded ? <div>{group.items.map(item_0 => {
        const medicine = item_0 as MedicineDetails;
        return <AnalogueRow key={medicine.medicine_id ?? medicine.id} medicine={medicine} group={group} nested={true} selected={medicine.medicine_id === selectedId} actionLabel={actionLabel} onActivate={() => onActivate(medicine)} onAction={onAction ? () => onAction(medicine) : undefined} />;
      })}</div> : null;
    $[31] = actionLabel;
    $[32] = expanded;
    $[33] = group;
    $[34] = onAction;
    $[35] = onActivate;
    $[36] = selectedId;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t13 || $[39] !== t14) {
    t15 = <li className="bg-card">{t13}{t14}</li>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
}
function _temp4(form) {
  return <DosageFormIcon key={form} form={form} className="text-muted-foreground size-4 shrink-0 self-center opacity-50" />;
}
function _temp3(item) {
  return item.dosage_form;
}
type DosageFormFilterBarProps = {
  options: DosageFormOption[];
  active: Set<string>;
  onToggle: (form: string) => void;
};
function DosageFormFilterBar(t0) {
  const $ = _c(9);
  const {
    options,
    active,
    onToggle
  } = t0;
  let t1;
  if ($[0] !== active || $[1] !== onToggle || $[2] !== options) {
    let t2;
    if ($[4] !== active || $[5] !== onToggle) {
      t2 = t3 => {
        const {
          form,
          count,
          label
        } = t3;
        const isActive = active.has(form);
        return <button key={form} type="button" aria-pressed={isActive} onClick={() => onToggle(form)} className={cn(filterToggleVariants({
          size: "xs"
        }), "capitalize")}><DosageFormIcon form={form} className="size-3.5" /><span className="truncate">{label}</span><span className="tabular-nums opacity-70">{count}</span></button>;
      };
      $[4] = active;
      $[5] = onToggle;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    t1 = options.map(t2);
    $[0] = active;
    $[1] = onToggle;
    $[2] = options;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[7] !== t1) {
    t2 = <div className="bg-card sticky top-0 z-10 flex shrink-0 flex-wrap gap-1.5 border-b px-5 pt-0 pb-2">{t1}</div>;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}